import type { MailMessageSummary } from "@/lib/types/mail";
import { decodeMimeWords } from "@/lib/mail/mimeHeaderDecode";
import {
  clearAccountBootstrapDone,
  clearFolderBootstrapDone,
} from "@/lib/cache/folderBootstrapStore";
import { persistInboxHeadersSession } from "@/lib/cache/inboxHeadersSessionCache";
import { INBOX_LIST_SIZE } from "@/lib/constants/inbox";
import { applyOptimisticMessages, isOptimisticGhostUid } from "@/lib/mail/mailMutationHold";

export function sanitizeListHeaders(messages: MailMessageSummary[]): MailMessageSummary[] {
  return messages.map((msg) => {
    const subject = decodeMimeWords(msg.subject, { collapseSpaces: true });
    const from = decodeMimeWords(msg.from, { collapseSpaces: true });
    const to = msg.to ? decodeMimeWords(msg.to, { collapseSpaces: true }) : msg.to;
    const next = {
      ...msg,
      subject: subject || msg.subject,
      from: from || msg.from,
      ...(to ? { to } : {}),
    };
    if (next.hasAttachments !== false) return next;
    const { hasAttachments: _ignored, ...rest } = next;
    return rest;
  });
}

export type FolderSnapshot = {
  accountId: number;
  folder: string;
  messages: MailMessageSummary[];
  total: number;
  offset: number;
  fromCache?: boolean;
  bootstrapped?: boolean;
};

function folderKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

const snapshots = new Map<string, FolderSnapshot>();
const listeners = new Map<string, Set<(snap: FolderSnapshot) => void>>();
const globalListeners = new Set<(snap: FolderSnapshot) => void>();

const VIEWED_STORAGE_KEY = "mail_folder_viewed_v1";
const MAX_VIEWED_KEYS = 200;

function loadViewedFromStorage(): Set<string> {
  if (typeof localStorage === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(VIEWED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((key): key is string => typeof key === "string" && key.length > 0)
    );
  } catch {
    return new Set();
  }
}

function persistViewedToStorage(): void {
  if (typeof localStorage === "undefined") return;
  try {
    const keys = [...viewedThisSession].slice(0, MAX_VIEWED_KEYS);
    if (keys.length === 0) {
      localStorage.removeItem(VIEWED_STORAGE_KEY);
      return;
    }
    localStorage.setItem(VIEWED_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    /* quota */
  }
}

/** Dossiers dont la liste a été ouverte (persisté F5 / nouvel onglet, pas un hydrate fond). */
const viewedThisSession = loadViewedFromStorage();

function trimSnapshot(entry: FolderSnapshot): FolderSnapshot {
  const sanitized = sanitizeListHeaders(entry.messages);
  const paginated =
    sanitized.length > INBOX_LIST_SIZE || entry.offset > INBOX_LIST_SIZE;

  if (paginated) {
    return {
      ...entry,
      messages: sanitized,
      offset: Math.max(entry.offset, sanitized.length),
    };
  }

  if (sanitized.length <= INBOX_LIST_SIZE) {
    return { ...entry, messages: sanitized };
  }

  return {
    ...entry,
    messages: sanitized.slice(0, INBOX_LIST_SIZE),
    offset: Math.min(entry.offset, INBOX_LIST_SIZE),
  };
}

function isSnapshotPersistable(
  entry: Pick<FolderSnapshot, "messages" | "total">
): boolean {
  if (entry.messages.length === 0 && entry.total > 0) {
    return false;
  }
  return true;
}

export function isFolderSnapshotWarm(snap: FolderSnapshot | null): boolean {
  if (!snap) return false;
  if (snap.total > 0 && snap.messages.length === 0) return false;
  if (snap.bootstrapped) return true;
  return snap.messages.length > 0 || snap.total === 0;
}

export function isFolderListViewedThisSession(accountId: number, folder: string): boolean {
  return viewedThisSession.has(folderKey(accountId, folder));
}

/**
 * Liste déjà posée dans la vue (compte/dossier ouvert).
 * Les pastilles peuvent alors descendre sous STATUS IMAP (lu local, IMAP en retard).
 * Persisté en localStorage : F5 / nouvel onglet gardent le 0 local (compte fermé).
 * Ne pas appeler depuis l’hydrate session multi-comptes.
 */
export function markFolderListViewedThisSession(accountId: number, folder: string): void {
  if (accountId < 1 || folder.length === 0) return;
  const key = folderKey(accountId, folder);
  if (viewedThisSession.has(key)) return;
  viewedThisSession.add(key);
  persistViewedToStorage();
  const snap = snapshots.get(key);
  if (!snap) return;
  for (const cb of globalListeners) {
    cb(snap);
  }
}

function persistSnapshotHeaders(accountId: number, folder: string): void {
  const snap = snapshots.get(folderKey(accountId, folder));
  if (!snap) return;
  persistInboxHeadersSession(accountId, folder, snap.messages, snap.total, snap.offset);
}

export function getFolderSnapshot(
  accountId: number,
  folder: string
): FolderSnapshot | null {
  const snap = snapshots.get(folderKey(accountId, folder)) ?? null;
  if (!snap) return null;
  const trimmed = trimSnapshot(snap);
  const messages = trimmed.messages.filter((message) => !isOptimisticGhostUid(message.uid));
  const cleaned =
    messages.length === trimmed.messages.length
      ? trimmed
      : {
          ...trimmed,
          messages,
          total: Math.max(0, trimmed.total - (trimmed.messages.length - messages.length)),
        };
  if (cleaned !== trimmed) snapshots.set(folderKey(accountId, folder), cleaned);
  if (cleaned.total > 0 && cleaned.messages.length === 0) {
    snapshots.delete(folderKey(accountId, folder));
    return null;
  }
  return cleaned;
}

export function publishFolderSnapshot(
  entry: Omit<FolderSnapshot, "messages"> & { messages: MailMessageSummary[] }
): void {
  const messages = applyOptimisticMessages(entry.accountId, entry.folder, entry.messages);
  const before = new Set(entry.messages.map((message) => Number(message.uid)));
  const after = new Set(messages.map((message) => Number(message.uid)));
  let removed = 0;
  for (const uid of before) {
    if (!after.has(uid)) removed += 1;
  }
  let added = 0;
  for (const uid of after) {
    if (!before.has(uid)) added += 1;
  }
  const nextEntry = {
    ...entry,
    messages,
    total: Math.max(0, entry.total - removed + added),
  };
  if (!isSnapshotPersistable(nextEntry)) {
    return;
  }
  const trimmed = trimSnapshot({
    ...nextEntry,
    messages: sanitizeListHeaders(nextEntry.messages),
  });
  const key = folderKey(entry.accountId, entry.folder);
  snapshots.set(key, trimmed);
  listeners.get(key)?.forEach((cb) => cb(trimmed));
  for (const cb of globalListeners) {
    cb(trimmed);
  }
}

/** Toute mise à jour de snapshot (pastilles sidebar alignées sur la liste). */
export function subscribeFolderSnapshotsChanged(
  callback: (snap: FolderSnapshot) => void
): () => void {
  globalListeners.add(callback);
  return () => {
    globalListeners.delete(callback);
  };
}

export function subscribeFolderSnapshot(
  accountId: number,
  folder: string,
  callback: (snap: FolderSnapshot) => void
): () => void {
  const key = folderKey(accountId, folder);
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(callback);
  return () => {
    set?.delete(callback);
    if (set?.size === 0) listeners.delete(key);
  };
}

export function clearFolderSnapshot(accountId: number, folder: string): void {
  const key = folderKey(accountId, folder);
  const had = snapshots.has(key);
  snapshots.delete(key);
  viewedThisSession.delete(key);
  persistViewedToStorage();
  clearFolderBootstrapDone(accountId, folder);
  if (!had) return;
  // Notifier les pastilles (sinon elles restent sur l’ancien merge / retombent sur IMAP périmé).
  const ghost: FolderSnapshot = {
    accountId,
    folder,
    messages: [],
    total: 0,
    offset: 0,
    fromCache: true,
    bootstrapped: false,
  };
  for (const cb of globalListeners) {
    cb(ghost);
  }
}

/** Met à jour les drapeaux lu/non lu dans le snapshot complet (filtre IMAP non lus). */
export function patchFolderSnapshotSeen(
  accountId: number,
  folder: string,
  uids: number[],
  seen: boolean
): void {
  const key = folderKey(accountId, folder);
  const snap = snapshots.get(key);
  if (!snap?.messages.length) return;

  const uidSet = new Set(uids.filter((uid) => uid > 0));
  if (uidSet.size === 0) return;

  let changed = false;
  const messages = snap.messages.map((m) => {
    if (!uidSet.has(m.uid) || m.seen === seen) return m;
    changed = true;
    return { ...m, seen };
  });
  if (!changed) return;

  publishFolderSnapshot({
    accountId: snap.accountId,
    folder: snap.folder,
    messages,
    total: snap.total,
    offset: snap.offset,
    // Optimiste local — ne pas traiter comme confirmation IMAP (sinon clear pending trop tôt).
    fromCache: true,
    bootstrapped: snap.bootstrapped,
  });
  persistSnapshotHeaders(accountId, folder);
}

/** Marque des UIDs comme corps prêts sur DD02 (après warm / bodies-status). */
export function patchFolderSnapshotBodyCached(
  accountId: number,
  folder: string,
  uids: number[]
): void {
  const key = folderKey(accountId, folder);
  const snap = snapshots.get(key);
  if (!snap?.messages.length) return;

  const uidSet = new Set(uids.filter((uid) => uid > 0));
  if (uidSet.size === 0) return;

  let changed = false;
  const messages = snap.messages.map((m) => {
    if (!uidSet.has(m.uid) || m.bodyCached === true) return m;
    changed = true;
    return { ...m, bodyCached: true };
  });
  if (!changed) return;

  publishFolderSnapshot({
    accountId: snap.accountId,
    folder: snap.folder,
    messages,
    total: snap.total,
    offset: snap.offset,
    fromCache: true,
    bootstrapped: snap.bootstrapped,
  });
}

/** Retire des messages du snapshot (réveil snooze, suppression optimiste). */
export function patchFolderSnapshotRemoveUids(
  accountId: number,
  folder: string,
  uids: number[]
): void {
  const key = folderKey(accountId, folder);
  const snap = snapshots.get(key);
  if (!snap?.messages.length) return;

  const uidSet = new Set(uids.map((uid) => Number(uid)).filter((uid) => uid > 0));
  if (uidSet.size === 0) return;

  const messages = snap.messages.filter((m) => !uidSet.has(Number(m.uid)));
  if (messages.length === snap.messages.length) return;

  publishFolderSnapshot({
    accountId: snap.accountId,
    folder: snap.folder,
    messages,
    total: Math.max(0, snap.total - (snap.messages.length - messages.length)),
    offset: snap.offset,
    // Mutation locale (delete / snooze) — la liste est la source, pas le cache IMAP.
    fromCache: false,
    bootstrapped: true,
  });
  persistSnapshotHeaders(accountId, folder);
}

export function clearAccountFolderSnapshots(accountId: number): void {
  const prefix = `${accountId}\0`;
  for (const key of [...snapshots.keys()]) {
    if (key.startsWith(prefix)) {
      snapshots.delete(key);
    }
  }
  for (const key of [...viewedThisSession]) {
    if (key.startsWith(prefix)) {
      viewedThisSession.delete(key);
    }
  }
  persistViewedToStorage();
  clearAccountBootstrapDone(accountId);
}

/** Vide tous les snapshots liste en mémoire (MAJ app / purge globale). */
export function clearAllFolderSnapshots(): void {
  snapshots.clear();
  viewedThisSession.clear();
  persistViewedToStorage();
}
